import { useCallback, useState } from 'react';
import { Editor, useEditorState } from '@tiptap/react';

import { useKeyPress } from '@/lib/hooks/use-key-press';

import { TextMenu } from './text-menu';
import { LinkMenu } from './link-menu';
import { VideoMenu } from './video-menu';

import styles from '../text-cell.module.scss';

interface PopupMenuProps {
  editor: Editor | null;
  isSelectedCell: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export const PopupMenu = ({ editor, isSelectedCell }: PopupMenuProps) => {
  const [isLinkMenuOpen, setIsLinkMenuOpen] = useState(false);
  const [isVideoMenuOpen, setIsVideoMenuOpen] = useState(false);

  const editorLinkState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isLinkActive: editor?.isActive('link') ?? undefined,
      linkHref: editor?.getAttributes('link').href ?? undefined,
    }),
  });

  const isLinkActive = editorLinkState?.isLinkActive ?? false;
  const currentHref = editorLinkState?.linkHref;

  const handleHideLinkMenu = useCallback(() => {
    setIsLinkMenuOpen(false);
    if (editor !== null) {
      editor.chain().focus().setTextSelection(editor.view.state.selection.to).run();
    }
  }, [editor]);

  const showLinkMenu = isLinkActive || isLinkMenuOpen;

  useKeyPress(
    ['k', 'meta'],
    (event) => {
      if (!isSelectedCell) {
        return;
      }

      event.preventDefault();
      setIsLinkMenuOpen(true);
    },
    { capture: true, includeInputs: true },
  );

  if (editor === null || !isSelectedCell) {
    return <></>;
  }

  return (
    <div className={styles.toolbar}>
      {showLinkMenu ? (
        <LinkMenu editor={editor} initialHref={currentHref} hideLinkMenu={handleHideLinkMenu} />
      ) : isVideoMenuOpen ? (
        <VideoMenu editor={editor} hideVideoMenu={() => setIsVideoMenuOpen(false)} />
      ) : (
        <TextMenu
          editor={editor}
          showLinkMenu={() => setIsLinkMenuOpen(true)}
          showVideoMenu={() => setIsVideoMenuOpen(true)}
        />
      )}
    </div>
  );
};
